import {css} from '@emotion/react'
import {useState} from 'react'
import debounce from 'lodash/debounce'
import {colors} from '../../../constants'
import {useOnce} from '../../../util/customHooks'
import SideBySide from './SideBySide'

const sideBySideData = {
    en: [
        {
            title: `Stealth`,
            description: `Introducing the most private browsing feature in the
    market - Stealth. Our truly private windows camouflage your device across all of your browsing.`,
        },
        {
            title: `Tracker Shield`,
            description: `Receive automatic tracker and cookie blocking. This prevents hidden trackers from collecting your data and slowing down your browsing.`,
        },
        {
            title: `Zap`,
            description: `Instantly clear your browsing history and close all tabs with one-click.`,
        },
        {
            title: `Secure Search`,
            description: `With Startpage as your default search engine, you’ll prevent re-targeted ads and profiled search results`,
        },
        {
            title: `Simple Setup`,
            description: `Easily import bookmarks, extensions, and saved passwords from your existing browser.`,
        },
    ],
    de: [
        {
            title: `Stealth`,
            description: `Der privateste Surfmodus: Stealth. Mit wirklich privaten Fenstern surfst du unter dem Radar – Stealth eben!`,
        },
        {
            title: `Trackerschutz`,
            description: `Mit Trackerschutz Tracker und Cookies automatisch blockiert. Das verhindert, dass versteckte Tracker deine Daten sammeln und deine Surferlebnis verlangsamen.`,
        },
        {
            title: `Zap`,
            description: `Lösche deinen Browserverlauf und schließe alle Tabs mit einem Klick.`,
        },
        {
            title: `Sichere Suche`,
            description: `Mit Startpage als Standardsuchmaschine vermeidest du personalisierte Werbung und profilbasierte Suchergebnisse`,
        },
        {
            title: `Einfache Einrichtung`,
            description: `Importiere ganz einfach deine Lesezeichen, Erweiterungen und gespeicherte Kennwörter aus anderen Browsern.`,
        },
    ],
    nl: [
        {
            title: `Stealth`,
            description: `De meest private browsing functionaliteit op de markt - Stealth. Onze privévensters camoufleren je apparaat bij het browsen.`,
        },
        {
            title: `Trackerschild`,
            description: `Ontvang automatische tracker- en cookieblokkering. Dit voorkomt dat verborgen trackers je gegevens verzamelen en het browsen vertragen.`,
        },
        {
            title: `Zap`,
            description: `Wis onmiddelijk je navigatiegeschiedenis en sluit alle tabbladen met één klik.`,
        },
        {
            title: `Veilig zoeken`,
            description: `Met Startpage als je standaard zoekmachine voorkom je gepersonaliseerde advertenties en geprofileerde zoekresultaten.`,
        },
        {
            title: `Eenvoudige installatie`,
            description: `Importeer gemakkelijk bladwijzers, extensies, en opgeslagen wachtwoorden uit je huidige browser.`,
        },
    ],
}

const FeatureSection = ({languageCode}) => {
    const [animationsData, setAnimationsData] = useState(new Array(sideBySideData[languageCode].length).fill(undefined))
    const [isSmallerWindow, setIsSmallerWindow] = useState(false)

    const titleBgColors = [colors.tealLight, colors.goldLight, colors.whisperDark]

    useOnce(async () => {
        const animationJsonFiles = [
            `https://www.startpage.com/sp/cdn/animations/startpage-browser/stealth-mode.json`,
            `https://www.startpage.com/sp/cdn/animations/startpage-browser/tracker-shield.json`,
            `https://www.startpage.com/sp/cdn/animations/startpage-browser/zap.json`,
            `https://www.startpage.com/sp/cdn/animations/startpage-browser/secure-search.json`,
            `https://www.startpage.com/sp/cdn/animations/startpage-browser/simple-setup.json`,
        ]
        setTimeout(async () => {
            const animationDataList = await Promise.all(
                animationJsonFiles.map(async (file) => {
                    return fetch(file).then((res) => res.json())
                }),
            )
            setAnimationsData(animationDataList)
        }, 1000)

        window.addEventListener(
            'resize',
            debounce(() => {
                if (window.innerWidth < 800) {
                    setIsSmallerWindow(true)
                } else {
                    setIsSmallerWindow(false)
                }
            }, 10),
        )
    })

    return (
        <section
            className="feature-section"
            css={css`
                max-width: 966px;
                margin: 0 auto 80px;
                padding: 3rem 2rem 0;
            `}
        >
            {sideBySideData[languageCode].map(({title, description}, i) => {
                let imgPosition = i % 2 === 0 ? 'right' : 'left'
                if (isSmallerWindow) {
                    imgPosition = 'left'
                }
                const animationJson = animationsData[i]
                const titleBgColor = titleBgColors[i % 3]
                return (
                    <SideBySide
                        animationJson={animationJson}
                        title={title}
                        titleBgColor={titleBgColor}
                        description={description}
                        imagePosition={imgPosition}
                        overrideStyles={css`
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            grid-gap: 25px;
                            grid-auto-flow: dense;
                            @media (max-width: 800px) {
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                            }
                            .side-by-side-image {
                                grid-column: ${imgPosition === 'left' ? 1 : 2};
                            }
                            .side-by-side-text {
                                grid-column: ${imgPosition === 'left' ? 2 : 1};
                            }
                        `}
                        key={i}
                    />
                )
            })}
        </section>
    )
}

export default FeatureSection
